/**
 * ProtectedRoute - Route guard for authenticated-only pages
 * Feature: 647-invite-registration (US4 - All Content Requires Authentication)
 *
 * Redirects unauthenticated users to login with returnUrl preserved.
 * Shows loading state while checking authentication.
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/services/auth';

interface ProtectedRouteProps {
  children: ReactNode;
}

/**
 * Route guard that requires authentication to view content.
 *
 * When user is not authenticated:
 * - Redirects to /login with returnUrl query param
 * - After login, user is redirected back to original URL
 *
 * @example
 * <Route path="/p/:photoId" element={
 *   <ProtectedRoute>
 *     <PhotoDetailPage />
 *   </ProtectedRoute>
 * } />
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          <p className="mt-2 text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // User is authenticated - render content
  if (user) {
    return <>{children}</>;
  }

  // Not authenticated - redirect to login with return URL
  const returnUrl = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
}
